import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';
import ResponsiveImage from '../common/ResponsiveImage';

// Helper component for the Chevron arrows
const ChevronArrow = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    className={`w-12 h-12 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path 
      d="M9 18L15 12L9 6" 
      stroke="#A1FF4F" 
      strokeWidth="4" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export default function About() {
  const [aboutTitle, setAboutTitle] = useState('Play. Talk. Stream.');
  const [aboutImage, setAboutImage] = useState('');
  const [aboutExtraImages, setAboutExtraImages] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [aboutParagraphs, setAboutParagraphs] = useState([
    'Play games, chill with the community, and talk about what is happening now.',
    'Then we stream the best moments.'
  ]);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('homepage_content').select('*').order('updated_at', { ascending: false }).limit(1);
      if (!error && data?.[0]) {
        if (data[0].about_title != null) setAboutTitle(data[0].about_title);
        if (Array.isArray(data[0].about_paragraphs)) setAboutParagraphs(data[0].about_paragraphs);
        if (data[0].about_image_url != null) setAboutImage(data[0].about_image_url);
        if (Array.isArray(data[0].about_gallery_images) && data[0].about_gallery_images.length) {
          setAboutExtraImages(data[0].about_gallery_images.filter(Boolean));
        }
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (aboutExtraImages.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % aboutExtraImages.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [aboutExtraImages.length]);

  useEffect(() => {
    if (carouselIndex >= aboutExtraImages.length) setCarouselIndex(0);
  }, [carouselIndex, aboutExtraImages.length]);

  return (
    <div className="min-h-screen bg-[#021206] text-white overflow-hidden font-sans flex items-center justify-center">
      
      <motion.section 
        id="about"
        className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-16 items-center"
        initial={{ opacity: 0, y: 50 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        
        {/* Left Column: Text */}
        <div className="space-y-12">
          <h2 className="text-6xl lg:text-7xl font-black uppercase tracking-tight text-[#A1FF4F]">
           {aboutTitle}
          </h2>
          
          <div className="space-y-8 text-white/80 leading-relaxed text-sm lg:text-base font-light">
            {aboutParagraphs.slice(0, 2).map((text, idx) => (
              <p key={idx}>{text}</p>
            ))}
          </div>
        </div>

        {/* Right Column: Image & Graphics */}
        <div className="relative mt-10 lg:mt-0">
            
          {/* Top Right Arrows - SYMMETRICAL */}
          <div className="absolute -top-12 right-0 flex space-x-[-15px] z-10" aria-hidden="true">
            <ChevronArrow />
            <ChevronArrow className="opacity-70" />
            <ChevronArrow className="opacity-40" />
          </div>

          {/* Image Container with Angled Clip */}
          <div className="relative w-full aspect-video lg:aspect-square">
            {/* The Image Mask */}
            <motion.div 
               className="w-full h-full bg-[#0A0E11] overflow-hidden"
               style={{ clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)' }}
               whileHover={{ scale: 1.02 }}
            >
              {aboutImage ? (
                <ResponsiveImage
                  src={aboutImage}
                  alt="Gamers playing"
                  className="w-full h-full object-cover"
                  sizes="(min-width: 1024px) 40vw, 92vw"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-white/50 text-sm">
                  No about image set
                </div>
              )}
            </motion.div>
          </div>

          {aboutExtraImages.length > 0 && (
            <div className="mt-4">
              <div className="relative h-28 md:h-36 rounded-xl overflow-hidden border border-[#A1FF4F]/20">
                <motion.img
                  key={aboutExtraImages[carouselIndex]}
                  src={aboutExtraImages[carouselIndex]}
                  alt={`About visual ${carouselIndex + 1}`}
                  initial={{ opacity: 0.2, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35 }}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex items-center justify-center gap-2 mt-3">
                {aboutExtraImages.map((img, idx) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setCarouselIndex(idx)}
                    className={`h-2.5 rounded-full transition-all ${idx === carouselIndex ? 'w-6 bg-[#A1FF4F]' : 'w-2.5 bg-white/40 hover:bg-white/70'}`}
                    aria-label={`Show about image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Small image strip */}
          {aboutExtraImages.length > 0 && <div className="grid grid-cols-3 gap-3 mt-3">
            {aboutExtraImages.map((img, idx) => (
              <motion.div
                key={img}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.08 }}
                className={`h-20 rounded-xl overflow-hidden border transition-colors ${idx === carouselIndex ? 'border-[#A1FF4F]' : 'border-[#A1FF4F]/20'}`}
              >
                <ResponsiveImage
                  src={img}
                  alt={`About visual ${idx + 1}`}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setCarouselIndex(idx)}
                  sizes="(min-width: 1024px) 12vw, 30vw"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            ))}
          </div>}

          {/* Bottom Left Arrows - SYMMETRICAL */}
          <div className="absolute bottom-[-20px] left-0 lg:left-20 flex space-x-[-15px] z-10" aria-hidden="true">
            <ChevronArrow />
            <ChevronArrow className="opacity-70" />
            <ChevronArrow className="opacity-40" />
          </div>

        </div>

      </motion.section>
    </div>
  );
}
